import { Button, Input } from '@components/common/Forms';
import { useState, useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { getMapCoordinatesFromAddress, getAddressFromCoordinates, LatLngLiteral } from '@utils/google-maps';
import {
  AdvancedMarker, APIProvider, Map, Pin,
} from '@vis.gl/react-google-maps';

/** Props */
interface JobAddressMapProps {
  googleApiKey: string,
  address?: string,
  changeAddress?: (value: string) => void;
  closeModal?: () => void;
}

/**
 * @description Job Address Map component.
 * - `googleApiKey` - Google API key.
 * - `address` - The job address.
 * - `changeAddress` - Change address function.
 * - `closeModal` - Close modal function.
 */
export const JobAddressMap = (props: JobAddressMapProps) => {
  // Destructure props
  const {
    googleApiKey,
    address,
    changeAddress,
    closeModal,
  } = props;

  // Translation
  const { t } = useTranslation();

  // Helper states
  // **************************
  const [renderMap, setRenderMap] = useState<boolean>(!address);
  const [position, setPosition] = useState<LatLngLiteral | undefined>({ lat: 45.9432, lng: 24.9668 });
  const [zoom, setZoom] = useState<number>(5);
  const [markerAddress, setMarkerAddress] = useState<string | undefined>(address);

  // Get positioning from address on map load
  // Prevents rendering the map before the position is set, it's ugly.
  // **************************
  const getPosition = useCallback(async (apiAddress?: string) => {
    if (apiAddress) {
      const newPosition = await getMapCoordinatesFromAddress(apiAddress);
      setPosition(newPosition);
      setZoom(15);
      setRenderMap(true);
    }
  }, []);

  // Get new address from coordinates
  // **************************
  const getNewAddress = useCallback(async (newPosition: LatLngLiteral | undefined) => {
    const newAddress = await getAddressFromCoordinates(newPosition);
    if (newAddress) setMarkerAddress(newAddress);
  }, []);

  // Render
  // **************************
  return (
    <APIProvider
      apiKey={googleApiKey}
      onLoad={() => { void getPosition(address); }}
    >
      {/* Map */}
      <div className="h-96 w-full">
        {renderMap && (
          <Map
            defaultCenter={position}
            defaultZoom={zoom}
            disableDefaultUI
            zoomControl
            zoomControlOptions={{ position: 8 }}
            mapId="JobAddressMap"
          >
            <AdvancedMarker
              draggable
              position={position}
              onDragEnd={(e) => {
                const newPosition = e?.latLng?.toJSON();
                void getNewAddress(newPosition);
              }}
            >
              <Pin
                background="#000000"
                glyphColor="#ffffff"
                borderColor="#000000"
                scale={1.35}
              />
            </AdvancedMarker>
          </Map>
        )}
      </div>

      {/* Footer */}
      <div className="flex gap-4 border-t bg-surface-50 px-6 py-4">
        {/* Marker address input */}
        <Input
          type="text"
          readOnly
          value={markerAddress}
          className="!border-input"
        />

        {/* Save button */}
        <Button
          size="sm"
          color="secondary"
          disabled={!markerAddress || !changeAddress}
          onClick={() => {
            if (changeAddress && markerAddress) {
              changeAddress(markerAddress);
              if (closeModal) closeModal();
            }
          }}
        >
          {t('add.job.map.confirm.location')}
        </Button>
      </div>
    </APIProvider>
  );
};
